import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { NOTEBOOK_ENTRIES, getNotebookByQuarter, getNotebookTotals } from '../data/editorialNotebook';
import { BreadcrumbsContainer } from '../components/seo/Breadcrumbs';
import LastReviewedStamp from '../components/seo/LastReviewedStamp';
import BuildPilotOpinion from '../components/editorial/BuildPilotOpinion';
import useJsonLd from '../hooks/useJsonLd';

/**
 * EditorialNotebookPage at /editorial-notebook.
 *
 * Sibling to /sources: where /sources lists the authorities BuildPilot
 * cites (the facts), the notebook lists BuildPilot's editorial readings
 * of those facts (the opinions). Together they make the editorial
 * separation visible at the URL level.
 *
 * Authorship attribution:
 *   Every entry is George Giannakakis's editorial reading. George
 *   personally holds the credentials (B.Arch.Studies, M.Arch UniSA,
 *   RLA300580) that make these opinions accountable. The notebook
 *   page is George's voice; BuildPilot the organisation publishes it.
 *
 * Legal posture:
 *   Page-level framing is explicit: editorial opinion / reading, not
 *   advice. The page footer points readers who want one-to-one advice
 *   to One on One Support (paid tier).
 */
const EditorialNotebookPage = () => {
  const { totalEntries, totalQuarters, totalTopics, latestDate } = getNotebookTotals();
  const byQuarter = getNotebookByQuarter();
  const quarters = Object.keys(byQuarter);

  useJsonLd(
    [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': 'https://buildpilot.com.au/editorial-notebook',
        url: 'https://buildpilot.com.au/editorial-notebook',
        name: `BuildPilot Editorial Notebook, ${totalEntries} editorial opinions across ${totalQuarters} quarter${totalQuarters === 1 ? '' : 's'}`,
        description: `The BuildPilot Editorial Notebook, a reverse-chronological digest of editorial opinions published by BuildPilot founder George Giannakakis. ${totalEntries} entries covering ${totalTopics} topical areas, refreshed quarterly. Companion to the BuildPilot Source Index at /sources.`,
        isPartOf: { '@id': 'https://buildpilot.com.au/#website' },
        dateModified: latestDate,
        inLanguage: 'en-AU',
        author: { '@id': 'https://buildpilot.com.au/#founder' },
        publisher: { '@id': 'https://buildpilot.com.au/#organization' },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: totalEntries,
          itemListElement: NOTEBOOK_ENTRIES.map((entry, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
              '@type': 'OpinionNewsArticle',
              headline: entry.title,
              datePublished: entry.date,
              author: { '@id': 'https://buildpilot.com.au/#founder' },
              about: entry.topic,
              url: `https://buildpilot.com.au${entry.sourceUrl}`,
            },
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://buildpilot.com.au' },
          { '@type': 'ListItem', position: 2, name: 'Editorial Notebook', item: 'https://buildpilot.com.au/editorial-notebook' },
        ],
      },
    ],
    'editorial-notebook-page'
  );

  return (
    <div className="min-h-screen bg-white" data-testid="editorial-notebook-page">
      <Helmet>
        <title>BuildPilot Editorial Notebook, our quarterly opinions on Adelaide building (2026)</title>
        <meta
          name="description"
          content={`${totalEntries} editorial opinions across ${totalTopics} topics, published by BuildPilot founder George Giannakakis (B.Arch.Studies, M.Arch UniSA, RLA300580). Refreshed quarterly. Companion to the BuildPilot Source Index.`}
        />
        <meta name="robots" content="index, follow, max-snippet:-1" />
        <link rel="canonical" href="https://buildpilot.com.au/editorial-notebook" />
        <meta property="og:title" content="BuildPilot Editorial Notebook, quarterly editorial opinions" />
        <meta property="og:description" content="The BuildPilot Editorial Notebook lists every editorial opinion published on the site, in reverse-chronological order. Refreshed quarterly." />
        <meta property="og:url" content="https://buildpilot.com.au/editorial-notebook" />
        <meta property="og:type" content="website" />
      </Helmet>

      <BreadcrumbsContainer items={[{ name: 'Editorial Notebook' }]} variant="light" />

      <header className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-8 sm:pb-10">
        <h1 className="text-[32px] sm:text-[44px] lg:text-[54px] font-bold text-[#1d1d1f] leading-[1.05] tracking-tight max-w-3xl">
          What BuildPilot has weighed in on this quarter.
        </h1>
        <p className="text-[15px] sm:text-[17px] text-[#5a5a5f] mt-4 sm:mt-5 max-w-2xl leading-relaxed">
          A reverse-chronological digest of editorial opinions published across BuildPilot. Each entry is the editorial reading of <Link to="/about/editorial-team" className="text-[#1d8efe] hover:underline font-medium">George Giannakakis</Link>, BuildPilot&rsquo;s founder, who personally holds the qualifications that make these opinions accountable: a Bachelor of Architectural Studies and a Master of Architecture (University of South Australia), and an active South Australian real estate licence (RLA300580). These are opinions, not advice. For one-to-one advice on your specific build, see <Link to="/one-on-one-support" className="text-[#1d8efe] hover:underline font-medium">One on One Support</Link>.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-gray-500">
          <LastReviewedStamp date={latestDate} className="!text-gray-500" />
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{totalEntries} editorial entries</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{totalQuarters} quarter{totalQuarters === 1 ? '' : 's'}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{totalTopics} topic areas</span>
        </div>
      </header>

      {/* How to read the notebook, editorial framing */}
      <section className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="bg-[#f5f7ff] border border-[#dce5ff] rounded-2xl p-5 sm:p-7" data-testid="notebook-framing">
          <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#1d8efe] mb-2">
            How to read the notebook
          </p>
          <p className="text-[14.5px] sm:text-[15.5px] text-[#1d1d1f] leading-relaxed">
            The <Link to="/sources" className="text-[#1d8efe] hover:underline font-medium">BuildPilot Source Index</Link> lists the authorities we cite (the facts). This notebook lists what BuildPilot has said about those facts (the opinions). Read them side-by-side and the editorial line between fact and opinion is explicit. Where you see &ldquo;in our reading&rdquo; or &ldquo;our experience&rdquo;, that is George&rsquo;s editorial view based on his work with Adelaide families. Where we cite a primary source, we link it.
          </p>
        </div>
      </section>

      <main className="max-w-[1080px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        {quarters.map((quarter) => (
          <section key={quarter} className="mb-12 sm:mb-14" data-testid={`notebook-quarter-${quarter.replace(/\s+/g, '-').toLowerCase()}`}>
            <h2 className="text-[20px] sm:text-[26px] font-bold text-[#1d1d1f] leading-tight mb-6 pb-3 border-b border-gray-200">
              {quarter}
              <span className="ml-3 text-[14px] sm:text-[16px] text-gray-400 font-normal">
                ({byQuarter[quarter].length})
              </span>
            </h2>

            <div className="space-y-5 sm:space-y-6">
              {byQuarter[quarter].map((entry) => (
                <article
                  key={entry.id}
                  className="bg-white border border-gray-200 hover:border-[#1d8efe] rounded-xl p-5 sm:p-6 transition-colors"
                  data-testid={`notebook-entry-${entry.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[#1d8efe]">
                      {entry.topic}
                    </span>
                    <time
                      dateTime={entry.date}
                      className="text-[11px] text-gray-400"
                    >
                      {new Date(entry.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </time>
                  </div>
                  <h3 className="text-[17px] sm:text-[20px] font-semibold text-[#1d1d1f] leading-snug mb-3">
                    {entry.title}
                  </h3>

                  <BuildPilotOpinion variant="subtle" showSourcesLink={false} className="!my-3">
                    {entry.excerpt}
                  </BuildPilotOpinion>

                  <Link
                    to={entry.sourceUrl}
                    className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1d8efe] hover:text-[#1670cc]"
                    data-testid={`notebook-entry-${entry.id}-link`}
                  >
                    Read in context, {entry.sourceLabel} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </article>
              ))}
            </div>
          </section>
        ))}

        {/* Closing block */}
        <aside className="mt-10 p-6 sm:p-8 bg-gray-50 rounded-2xl border border-gray-100" data-testid="notebook-closing">
          <h2 className="text-[16px] sm:text-[18px] font-semibold text-[#1d1d1f] mb-2">
            Want our editorial reading on your specific build?
          </h2>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#5a5a5f] leading-relaxed">
            The notebook is BuildPilot&rsquo;s public editorial layer; useful as a thinking tool, not as advice on your specific situation. If you want a one-to-one editorial reading of your block, your budget, your shortlist or your contract, <Link to="/one-on-one-support" className="text-[#1d8efe] hover:underline font-medium">One on One Support</Link> is our paid tier where George walks you through it personally. You can also browse the <Link to="/sources" className="text-[#1d8efe] hover:underline font-medium">BuildPilot Source Index</Link> for the authorities behind every fact we cite.
          </p>
        </aside>
      </main>
    </div>
  );
};

export default EditorialNotebookPage;
